<template>
  <div class="metric-row" data-testid="metric-row">
    <div class="metric-row__header">
      <Icon icon="mdi-drag" class="metric-row__handle cursor-grab" />
      <Typography variant="subtitle-1" class="metric-row__label">Row {{ index + 1 }}</Typography>
      <v-menu>
        <template v-slot:activator="{ on, attrs }">
          <Button
            text
            small
            iconRight="mdi-menu-down"
            data-testid="column-count-menu"
            v-bind="attrs"
            v-on="on"
          >
            {{ columnCount }} Col
          </Button>
        </template>
        <v-list>
          <v-list-item
            v-for="option in columnOptions"
            :key="option"
            :data-testid="`column-count-${option}`"
            @click="$emit('update:columnCount', option)"
          >
            <Typography variant="body-2">{{ option }} Col</Typography>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer />
      <div class="metric-row__actions">
        <Button icon small data-testid="delete-row" @click="requestDeleteRow">
          <Icon icon="mdi-delete" color="darkGrey" small />
        </Button>
      </div>
    </div>

    <div class="metric-row__columns">
      <template v-for="slotIndex in slotIndexes">
        <MetricColumn
          v-if="columnForSlot(slotIndex)"
          :key="slotIndex"
          :column="columnForSlot(slotIndex)"
          @edit="$emit('edit-metric', slotIndex)"
          @delete="requestDeleteMetric(slotIndex)"
        />
        <AddMetricButton
          v-else
          :key="slotIndex + '-add-metric-button'"
          @click="$emit('edit-metric', slotIndex)"
        />
      </template>
    </div>

    <ErrorDialogV2
      v-model="confirmOpen"
      :title="deleteConfirmTitle"
      :message="deleteConfirmMessage"
      confirm-button-text="Delete"
      cancel-button-text="Cancel"
      show-cancel-button
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import MetricColumn from "./MetricColumn.vue";
import AddMetricButton from "./AddMetricButton.vue";
import ErrorDialogV2 from "@/components/ErrorDialogV2.vue";
import { metricComponentLabel } from "./AddMetricSideSheet/metricOptions";

export default {
  name: "MetricRow",
  components: { Typography, Button, Icon, MetricColumn, AddMetricButton, ErrorDialogV2 },
  props: {
    index: { type: Number, required: true },
    columnCount: { type: Number, default: 1 },
    // Configured columns only; a row may leave slots empty.
    columns: { type: Array, default: () => [] },
  },
  emits: ["update:columnCount", "delete", "edit-metric", "delete-metric"],
  data() {
    return {
      columnOptions: [1, 2, 3],
      // What the confirm is pointed at: { type: "row" } or
      // { type: "metric", slotIndex }. Null closes the dialog.
      pendingDelete: null,
    };
  },
  computed: {
    slotIndexes() {
      return Array.from({ length: this.columnCount }, (_, index) => index);
    },
    confirmOpen: {
      get() {
        return !!this.pendingDelete;
      },
      // Covers both dialog buttons and a backdrop dismiss.
      set(open) {
        if (!open) this.pendingDelete = null;
      },
    },
    deleteConfirmTitle() {
      if (this.pendingDelete?.type === "row") return `Delete Row ${this.index + 1}?`;
      const column = this.columnForSlot(this.pendingDelete?.slotIndex);
      const label = column ? metricComponentLabel(column.componentType) : "";
      return label ? `Delete this ${label} metric?` : "Delete this metric?";
    },
    deleteConfirmMessage() {
      return this.pendingDelete?.type === "row"
        ? "The row and any metrics in it will be removed."
        : "This metric will be removed from the row.";
    },
  },
  methods: {
    // Slot 0 is valid, so this is an explicit null check rather than a falsy one.
    columnForSlot(slotIndex) {
      if (slotIndex === null || slotIndex === undefined) return null;
      return this.columns.find((column) => column.slotIndex === slotIndex) ?? null;
    },
    requestDeleteRow() {
      this.pendingDelete = { type: "row" };
    },
    requestDeleteMetric(slotIndex) {
      this.pendingDelete = { type: "metric", slotIndex };
    },
    confirmDelete() {
      const target = this.pendingDelete;
      if (!target) return;
      if (target.type === "row") this.$emit("delete");
      else this.$emit("delete-metric", target.slotIndex);
      this.pendingDelete = null;
    },
  },
};
</script>

<style scoped>
.metric-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 10px 16px 16px;
  border: 2px solid var(--v-grey-base);
  border-radius: 4px;
  background: white;
}

.metric-row__header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.metric-row__label {
  flex-shrink: 0;
}

.metric-row__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.metric-row__columns {
  display: flex;
  align-items: stretch;
  gap: 16px;
}
</style>
